import React, { useEffect, useMemo, useState } from 'react';
import './DismissibleNotice.css';

const STORAGE_PREFIX = 'fatcat:dismissed-notice:v1';

function saoPauloDateKey(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = type => parts.find(part => part.type === type)?.value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function storageScope() {
  try {
    return window.localStorage.getItem('userId') || 'anonymous';
  } catch (_) {
    return 'anonymous';
  }
}

function readCookie(key) {
  try {
    const prefix = `${encodeURIComponent(key)}=`;
    const item = document.cookie.split('; ').find(cookie => cookie.startsWith(prefix));
    return item ? decodeURIComponent(item.slice(prefix.length)) : null;
  } catch (_) {
    return null;
  }
}

function readStoredValue(storageKey) {
  try {
    const value = window.localStorage.getItem(storageKey);
    if (value) return value;
  } catch (_) {}
  try {
    const value = window.sessionStorage.getItem(storageKey);
    if (value) return value;
  } catch (_) {}
  return readCookie(storageKey);
}

function persistStoredValue(storageKey, value) {
  try {
    window.localStorage.setItem(storageKey, value);
  } catch (_) {}
  try {
    window.sessionStorage.setItem(storageKey, value);
  } catch (_) {}
  try {
    document.cookie = `${encodeURIComponent(storageKey)}=${encodeURIComponent(value)}; Max-Age=172800; Path=/; SameSite=Lax`;
  } catch (_) {}
}

function dismissedForCurrentEvent(storageKey, eventKey) {
  try {
    const saved = JSON.parse(readStoredValue(storageKey) || 'null');
    return saved?.date === saoPauloDateKey() && saved?.eventKey === eventKey;
  } catch (_) {
    return false;
  }
}

export default function DismissibleNotice({
  noticeKey,
  eventKey,
  closeLabel = 'Fechar aviso',
  className = '',
  children,
  ...props
}) {
  const normalizedEventKey = String(eventKey ?? '');
  const storageKey = useMemo(
    () => `${STORAGE_PREFIX}:${storageScope()}:${noticeKey}`,
    [noticeKey],
  );
  const [dismissed, setDismissed] = useState(
    () => dismissedForCurrentEvent(storageKey, normalizedEventKey),
  );

  useEffect(() => {
    setDismissed(dismissedForCurrentEvent(storageKey, normalizedEventKey));
  }, [storageKey, normalizedEventKey]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDismissed(dismissedForCurrentEvent(storageKey, normalizedEventKey));
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [storageKey, normalizedEventKey]);

  if (dismissed) return null;

  const dismiss = () => {
    persistStoredValue(storageKey, JSON.stringify({
      date: saoPauloDateKey(),
      eventKey: normalizedEventKey,
    }));
    setDismissed(true);
  };

  return (
    <div {...props} className={`fatcat-dismissible-notice ${className}`.trim()}>
      {children}
      <button
        type="button"
        className="fatcat-dismissible-notice__close"
        aria-label={closeLabel}
        title="Fechar aviso"
        onClick={dismiss}
      >
        ×
      </button>
    </div>
  );
}

export { saoPauloDateKey };